import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Chess } from 'chess.js';
import ChessBoardView from '@/components/chess/ChessBoardView';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Crosshair, Shield, User, Eraser, RotateCcw } from 'lucide-react';

// Roles supported by the capture builder.
//   student  -> the piece the student controls (single instance, real chess piece)
//   target   -> object the student must capture (emoji overlay)
//   obstacle -> static piece blocking the student's path (emoji overlay)
const ROLES = [
  { key: 'student',  label: 'Mi Pieza',   icon: User,      hex: 'rgba(37, 99, 235, 0.35)' },
  { key: 'target',   label: 'Objeto',     icon: Crosshair, hex: 'rgba(220, 38, 38, 0.30)' },
  { key: 'obstacle', label: 'Obstáculo',  icon: Shield,    hex: 'rgba(100, 116, 139, 0.30)' },
  { key: 'erase',    label: 'Borrar',     icon: Eraser,    hex: null },
];

// Real chess pieces (rendered by react-chessboard via FEN).
// Unicode glyphs are used in the palette so teachers can see the piece at a glance.
const PIECES = [
  { code: 'P', label: 'Peón',    w: '♙', b: '♟' },
  { code: 'N', label: 'Caballo', w: '♘', b: '♞' },
  { code: 'B', label: 'Alfil',   w: '♗', b: '♝' },
  { code: 'R', label: 'Torre',   w: '♖', b: '♜' },
  { code: 'Q', label: 'Dama',    w: '♕', b: '♛' },
  { code: 'K', label: 'Rey',     w: '♔', b: '♚' },
];
const COLORS = [{ code: 'w', label: 'Blancas' }, { code: 'b', label: 'Negras' }];

// Emoji library — used for "Objeto" and "Obstáculo". These are rendered as SVG
// background-images on the board square (so they never clash with the FEN piece layer).
const OBJECT_CATEGORIES = [
  { key: 'food',    label: 'Comida',    items: ['🍕','🍔','🍩','🍎','🍓','🍦','🍪','🥐','🍰','🧁'] },
  { key: 'animals', label: 'Animales',  items: ['🐶','🐱','🦁','🐼','🐸','🦊','🐰','🦄','🐢','🐝'] },
  { key: 'toys',    label: 'Juguetes',  items: ['🧸','🪀','🎈','🎲','🪁','🚂','⚽','🏀','🎮','🪅'] },
  { key: 'prizes',  label: 'Premios',   items: ['🏆','🥇','🥈','🥉','🏅','🎖️','👑','💎','⭐','🎁'] },
  { key: 'emojis',  label: 'Emojis',    items: ['😀','😎','🤩','😍','🥳','🤖','👻','🎃','💩','👽'] },
  { key: 'space',   label: 'Espacio',   items: ['🚀','🛸','🪐','🌟','☄️','🌙','🌍','🌞','⭐','👽'] },
];
const OBSTACLE_ITEMS = ['🧱','📦','🚧','🪨','🌵','🔥','⚠️','🚫'];

const START = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

// Only chess-piece roles ("student") land on the FEN. Emoji objects/obstacles
// are painted on top of the square via background-image so react-chessboard
// keeps rendering the real piece sprites for the student's piece.
function piecesToFen(pieces, turn = 'w') {
  const map = {};
  (pieces || []).forEach(p => {
    if (p.piece && p.color) map[p.square] = p;
  });
  const ranks = [];
  for (let r = 8; r >= 1; r--) {
    let row = '', empty = 0;
    for (let f = 0; f < 8; f++) {
      const sq = 'abcdefgh'[f] + r;
      const cell = map[sq];
      if (cell) {
        if (empty > 0) { row += empty; empty = 0; }
        const p = cell.piece.toLowerCase();
        row += cell.color === 'w' ? p.toUpperCase() : p;
      } else empty++;
    }
    if (empty > 0) row += empty;
    ranks.push(row);
  }
  return ranks.join('/') + ` ${turn} - - 0 1`;
}

// Render an emoji as a repeat-free centered SVG background-image so it can be
// composited on top of the chessboard square. 68% keeps it inside the cell.
function emojiToBg(emoji) {
  const safe = String(emoji).replace(/"/g, '');
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><text x='50' y='55' text-anchor='middle' dominant-baseline='central' font-size='68'>${safe}</text></svg>`;
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
}

export default function CaptureActivityBuilder({ open, onClose, onSave, initial }) {
  const editingId = initial?.editingId || null;
  const existing = initial?.activity || null;

  const [title, setTitle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [points, setPoints] = useState(50);
  const [maxAttempts, setMaxAttempts] = useState(3);
  const [timeSeconds, setTimeSeconds] = useState(120);
  const [turn, setTurn] = useState('w');

  const [pieces, setPieces] = useState([]); // union: {square,role,piece,color} | {square,role,emoji}
  const [mode, setMode] = useState('student');

  // Piece palette state (for Mi Pieza)
  const [pieceCode, setPieceCode] = useState('N');
  const [colorCode, setColorCode] = useState('w');

  // Emoji palette state
  const [objectCategory, setObjectCategory] = useState('food');
  const [selectedEmoji, setSelectedEmoji] = useState('🍕');
  const [selectedObstacle, setSelectedObstacle] = useState('🧱');

  useEffect(() => {
    if (!open) return;
    if (existing) {
      const cfg = existing.capture_config || null;
      setTitle(existing.title || '');
      setInstructions(existing.instructions || cfg?.instructions || '');
      setPoints(existing.points ?? 50);
      setMaxAttempts(existing.max_attempts ?? 3);
      setTimeSeconds(existing.time_seconds ?? 120);
      setTurn(cfg?.turn || 'w');
      setPieces(Array.isArray(cfg?.pieces) ? cfg.pieces.map(p => ({ ...p })) : []);
    } else {
      setTitle(''); setInstructions(''); setPoints(50); setMaxAttempts(3); setTimeSeconds(120);
      setTurn('w'); setPieces([]);
    }
    setMode('student');
    setPieceCode('N'); setColorCode('w');
    setObjectCategory('food'); setSelectedEmoji('🍕'); setSelectedObstacle('🧱');
  }, [open, existing]);

  const fen = useMemo(() => piecesToFen(pieces, turn), [pieces, turn]);

  const extraSquareStyles = useMemo(() => {
    const styles = {};
    pieces.forEach(p => {
      const roleDef = ROLES.find(r => r.key === p.role);
      const base = {};
      if (roleDef?.hex) base.backgroundColor = roleDef.hex;
      if (p.emoji) {
        base.backgroundImage = emojiToBg(p.emoji);
        base.backgroundRepeat = 'no-repeat';
        base.backgroundPosition = 'center';
        base.backgroundSize = '78% 78%';
      }
      if (Object.keys(base).length) {
        styles[p.square] = { ...base, transition: 'background-color 150ms ease' };
      }
    });
    return styles;
  }, [pieces]);

  const handleSquareClick = useCallback((square) => {
    setPieces(prev => {
      if (mode === 'erase') return prev.filter(p => p.square !== square);
      const withoutSquare = prev.filter(p => p.square !== square);
      let cleaned = withoutSquare;
      if (mode === 'student') cleaned = cleaned.filter(p => p.role !== 'student');
      if (mode === 'student') {
        return [...cleaned, { square, role: 'student', piece: pieceCode, color: colorCode }];
      }
      if (mode === 'target') {
        return [...cleaned, { square, role: 'target', emoji: selectedEmoji }];
      }
      if (mode === 'obstacle') {
        return [...cleaned, { square, role: 'obstacle', emoji: selectedObstacle }];
      }
      return prev;
    });
  }, [mode, pieceCode, colorCode, selectedEmoji, selectedObstacle]);

  const clearBoard = () => { if (window.confirm('¿Vaciar el tablero?')) setPieces([]); };

  const counts = useMemo(() => {
    const c = { student: 0, target: 0, obstacle: 0 };
    pieces.forEach(p => { if (c[p.role] !== undefined) c[p.role]++; });
    return c;
  }, [pieces]);

  // Validación preventiva: refleja EXACTAMENTE el contrato mínimo que exige el
  // runner del alumno (QuizCaptureRunner.hasValidConfig → capture_config.pieces
  // con ≥1 rol "student" y ≥1 rol "target"). Si esto no se cumple, la actividad
  // renderizaría "posición no válida" y podría bloquear el Quiz, así que la
  // bloqueamos aquí ANTES de guardar. No validamos el FEN con chess.js a
  // propósito: los ejercicios de captura suelen no tener reyes y chess.js
  // rechazaría posiciones legítimas.
  const validationIssues = useMemo(() => {
    const issues = [];
    if (!title.trim()) issues.push('Falta el título de la actividad.');
    if (counts.student === 0) issues.push('Falta la pieza del alumno: elige el rol «Mi Pieza» y colócala en el tablero.');
    if (counts.target === 0) issues.push('Falta al menos un objeto para capturar: elige el rol «Objeto» y coloca uno en el tablero.');
    return issues;
  }, [title, counts]);

  const handleSave = async () => {
    if (validationIssues.length > 0) { toast.error(validationIssues[0]); return; }
    try { new Chess(fen); } catch { /* python-chess side accepts, chess.js is stricter; ignore */ }
    const student = pieces.find(p => p.role === 'student');
    const targetSquares = pieces.filter(p => p.role === 'target').map(p => p.square);
    const payload = {
      type: 'capture',
      title: title.trim(),
      points: Number(points) || 0,
      max_attempts: Number(maxAttempts) || 1,
      time_seconds: Number(timeSeconds) || 60,
      fen,
      instructions: instructions.trim(),
      capture_mode: 'count',
      target_square: targetSquares[0] || '',
      target_count: targetSquares.length || 1,
      capture_config: {
        version: 1,
        turn,
        pieces,
        student_square: student?.square || null,
        target_squares: targetSquares,
        obstacle_squares: pieces.filter(p => p.role === 'obstacle').map(p => p.square),
        instructions: instructions.trim(),
      },
    };
    await onSave(payload, editingId);
  };

  const currentCategory = OBJECT_CATEGORIES.find(c => c.key === objectCategory) || OBJECT_CATEGORIES[0];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent data-testid="capture-builder" className="max-w-6xl max-h-[94vh] overflow-hidden flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle>{editingId ? 'Editar' : 'Nueva'} · Capture Activity</DialogTitle>
          <DialogDescription>Coloca la pieza del alumno, los objetos a capturar y los obstáculos.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[340px_minmax(0,1fr)] gap-5 items-start overflow-hidden">

          {/* Left: board + compact settings */}
          <div className="space-y-3">
            <div className="grid place-items-center">
              <ChessBoardView
                fen={fen || START}
                boardWidth={340}
                arePiecesDraggable={false}
                onSquareClick={handleSquareClick}
                extraSquareStyles={extraSquareStyles}
              />
            </div>

            <div className="space-y-1">
              <div className="grid grid-cols-2 gap-1.5">
                <div className="flex items-center gap-1.5">
                  <Label className="shrink-0 text-xs">Turno</Label>
                  <div className="grid grid-cols-2 gap-1 flex-1">
                    {COLORS.map(c => (
                      <button
                        key={c.code}
                        data-testid={`cap-turn-${c.code}`}
                        onClick={() => setTurn(c.code)}
                        className={`py-1 rounded-lg border text-xs font-semibold ${
                          turn === c.code
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                            : 'border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-1.5">
                  <Label className="shrink-0 text-xs">Puntaje</Label>
                  <Input
                    data-testid="cap-points"
                    className="h-8 flex-1"
                    type="number"
                    value={points}
                    onChange={e => setPoints(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-1.5">
                <div className="flex items-center gap-1.5">
                  <Label className="shrink-0 text-xs">Intentos</Label>
                  <Input
                    data-testid="cap-attempts"
                    className="h-8 flex-1"
                    type="number"
                    value={maxAttempts}
                    onChange={e => setMaxAttempts(e.target.value)}
                  />
                </div>

                <div className="flex items-center gap-1.5">
                  <Label className="shrink-0 text-xs">Tiempo</Label>
                  <Input
                    data-testid="cap-time"
                    className="h-8 flex-1"
                    type="number"
                    value={timeSeconds}
                    onChange={e => setTimeSeconds(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between text-[11px] leading-4 text-slate-500 px-1 pt-0.5">
              <div>
                Mi pieza: <span className="font-semibold text-slate-700">{counts.student}</span>
                {' · '}
                Objetos: <span className="font-semibold text-rose-600">{counts.target}</span>
                {' · '}
                Obstáculos: <span className="font-semibold text-slate-700">{counts.obstacle}</span>
              </div>
              <button
                data-testid="cap-clear"
                onClick={clearBoard}
                className="inline-flex items-center gap-1 text-slate-500 hover:text-rose-600"
              >
                <RotateCcw className="w-3.5 h-3.5"/> Vaciar
              </button>
            </div>
          </div>

          {/* Right: metadata + tools */}
          <div className="space-y-2 min-w-0">

            <div>
              <Label>Título</Label>
              <Input
                data-testid="cap-title"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Ej: Captura el peón evitando los obstáculos"
              />
            </div>

            <div>
              <Label>Instrucciones</Label>
              <Textarea
                data-testid="cap-instructions"
                rows={2}
                value={instructions}
                onChange={e => setInstructions(e.target.value)}
                placeholder="Explica al alumno qué debe hacer…"
              />
            </div>

            {/* Capture Activity tools — same logic, new final layout */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-2 shadow-sm">
              <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                Herramientas
              </div>

              <div className="grid grid-cols-4 gap-2">
                {ROLES.map(r => (
                  <button
                    key={r.key}
                    data-testid={`cap-mode-${r.key}`}
                    onClick={() => setMode(r.key)}
                    className={`rounded-xl border px-2 py-1.5 text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
                      mode === r.key
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-sm'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <r.icon className="w-4 h-4" />
                    {r.key === 'target' ? 'Objetos' : r.label}
                  </button>
                ))}
              </div>

              <div className="mt-2 rounded-xl border border-slate-200 bg-white p-2">
                {mode === 'student' && (
                  <div className="space-y-1.5">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                      Pieza del alumno
                    </div>

                    <div className="grid grid-cols-6 gap-1.5">
                      {PIECES.map(p => (
                        <button
                          key={p.code}
                          data-testid={`cap-piece-${p.code}`}
                          onClick={() => setPieceCode(p.code)}
                          title={p.label}
                          className={`h-12 rounded-lg border flex items-center justify-center leading-none transition-all ${
                            pieceCode === p.code
                              ? 'border-indigo-500 bg-indigo-50 shadow-sm'
                              : 'border-slate-200 bg-white hover:bg-slate-50'
                          }`}
                        >
                          <span className="text-2xl">
                            {colorCode === 'w' ? p.w : p.b}
                          </span>
                        </button>
                      ))}
                    </div>

                    <div className="grid grid-cols-2 gap-1.5">
                      {COLORS.map(c => (
                        <button
                          key={c.code}
                          data-testid={`cap-color-${c.code}`}
                          onClick={() => setColorCode(c.code)}
                          className={`py-1.5 rounded-lg border text-xs font-semibold transition-all ${
                            colorCode === c.code
                              ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                              : 'border-slate-200 bg-white hover:bg-slate-50'
                          }`}
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {mode === 'target' && (
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                        Objetos
                      </div>
                      <div className="text-lg leading-none">{selectedEmoji}</div>
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                      {OBJECT_CATEGORIES.map(cat => (
                        <button
                          key={cat.key}
                          data-testid={`cap-cat-${cat.key}`}
                          onClick={() => {
                            setObjectCategory(cat.key);
                            setSelectedEmoji(cat.items[0]);
                          }}
                          className={`px-2.5 py-1.5 rounded-lg border text-[11px] font-semibold transition-all ${
                            objectCategory === cat.key
                              ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                              : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-600'
                          }`}
                        >
                          {cat.label}
                        </button>
                      ))}
                    </div>

                    <div className="grid grid-cols-10 gap-1.5">
                      {currentCategory.items.map((emo, i) => (
                        <button
                          key={`${currentCategory.key}-${i}`}
                          data-testid={`cap-emoji-${currentCategory.key}-${i}`}
                          onClick={() => setSelectedEmoji(emo)}
                          className={`aspect-square rounded-lg border text-xl leading-none flex items-center justify-center transition-all ${
                            selectedEmoji === emo
                              ? 'border-indigo-500 bg-indigo-50 shadow-sm'
                              : 'border-slate-200 bg-white hover:bg-slate-50'
                          }`}
                        >
                          {emo}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {mode === 'obstacle' && (
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                        Obstáculos
                      </div>
                      <div className="text-lg leading-none">{selectedObstacle}</div>
                    </div>

                    <div className="grid grid-cols-8 gap-1.5">
                      {OBSTACLE_ITEMS.map((emo, i) => (
                        <button
                          key={`obs-${i}`}
                          data-testid={`cap-obstacle-${i}`}
                          onClick={() => setSelectedObstacle(emo)}
                          className={`aspect-square rounded-lg border text-xl leading-none flex items-center justify-center transition-all ${
                            selectedObstacle === emo
                              ? 'border-indigo-500 bg-indigo-50 shadow-sm'
                              : 'border-slate-200 bg-white hover:bg-slate-50'
                          }`}
                        >
                          {emo}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {mode === 'erase' && (
                  <div className="text-xs text-slate-500 rounded-lg border border-dashed border-slate-300 p-3 text-center">
                    Haz clic en cualquier casilla para eliminar la pieza, objeto u obstáculo.
                  </div>
                )}
              </div>
            </div>

            {validationIssues.length > 0 && (
              <div
                data-testid="cap-validation"
                className="rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-xs px-3 py-1.5 space-y-0.5"
              >
                <div className="font-semibold">Esta actividad Capture todavía no puede guardarse:</div>
                <ul className="list-disc pl-4 space-y-0.5">
                  {validationIssues.map((m, i) => (
                    <li key={i} data-testid={`cap-validation-item-${i}`}>{m}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="text-[11px] leading-4 text-slate-500 border-t pt-1.5">
              Tip: elige el rol (Mi Pieza · Objeto · Obstáculo), después la pieza o el emoji,
              y haz clic en la casilla del tablero para colocarlo. Usa <span className="font-semibold">Borrar</span> para quitar.
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0 pt-2">
          <Button variant="outline" onClick={() => onClose?.()}>Cancelar</Button>
          <Button data-testid="cap-save" onClick={handleSave} disabled={validationIssues.length > 0} title={validationIssues[0] || ''} className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">{editingId ? 'Guardar' : 'Agregar al Study'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
